import { useMemo, useState, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bookmark, ExternalLink, ImageOff, Loader2 } from "lucide-react";
import type { BlogContentBlock, BlogLinksBox, BlogStashCard, BlogStashGrid } from "@/lib/blog";
import { storeDestinationForUrl } from "@/lib/storeLinks";
import { trackMarketingCtaClick } from "@/services/marketingCtaAnalytics.service";
import { STASH_DETAIL } from "@/routes";
import { postsService, type Post } from "@/services/posts.service";
import { StashThumbnail, type StashThumbnailData } from "@/components/shared/StashThumbnail";
import {
  EXAMPLE_POSTS,
  THEME_LABELS,
  type ExampleTheme,
} from "@/components/shared/ExamplesSection";
import { getPlatformIcon } from "@/components/shared/PlatformIcon";
import { BlogCollectionCardBlock } from "./BlogCollectionCardBlock";
import { BlogInlineCta } from "./BlogInlineCta";

const BLOG_STASH_VISUAL_ASPECT = "aspect-[4/5] sm:aspect-auto sm:h-full sm:min-h-[260px]";

const toThumbnailData = (post: Post): StashThumbnailData => {
  const enrichment = post as Post & { main_enrichment_response?: StashThumbnailData["main_enrichment_response"] };

  return {
    id: post.stasht_id ?? post.id,
    title: post.title,
    user_title: post.user_title,
    content: post.content as StashThumbnailData["content"],
    platform: post.platform ?? post.source?.platform,
    source: post.source as StashThumbnailData["source"],
    main_enrichment_response: enrichment.main_enrichment_response,
    unfurl_status: post.unfurl_status,
  };
};

const BlogStashVisual = ({
  card,
  post,
  isLoading,
  isError,
}: {
  card: BlogStashCard;
  post?: Post;
  isLoading: boolean;
  isError: boolean;
}) => {
  if (post) {
    return (
      <StashThumbnail
        post={{ ...toThumbnailData(post), title: card.title, user_title: card.title }}
        href={STASH_DETAIL({ stashId: card.id })}
        width="full"
        cornerRadius="lg"
        aspectRatio={BLOG_STASH_VISUAL_ASPECT}
        showTitle
        loading="lazy"
        className="h-full w-full"
      />
    );
  }

  return (
    <div className="flex aspect-[4/5] w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-foreground/15 bg-muted/30 text-center text-xs text-foreground/50 sm:aspect-auto sm:h-full sm:min-h-[260px]">
      {isLoading ? (
        <>
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading visual
        </>
      ) : (
        <>
          <ImageOff className="h-5 w-5" />
          {isError ? "Visual failed to load" : "Visual unavailable"}
        </>
      )}
    </div>
  );
};

const BlogStashCardBlock = ({
  card,
  post,
  isLoading,
  isError,
}: {
  card: BlogStashCard;
  post?: Post;
  isLoading: boolean;
  isError: boolean;
}) => (
  <aside className="not-prose rounded-lg border border-foreground/10 bg-card p-4 sm:p-5 shadow-sm">
    <div className="grid gap-4 sm:grid-cols-[150px_minmax(0,1fr)] sm:items-stretch">
      <BlogStashVisual card={card} post={post} isLoading={isLoading} isError={isError} />
      <div className="flex min-w-0 flex-col gap-2">
        <p className="m-0 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-brand-500">
          <Bookmark className="h-3.5 w-3.5" />
          Saved on Stasht
        </p>
        <div className="flex flex-col gap-1">
          <h3 className="m-0 text-xl font-semibold leading-snug text-foreground">{card.title}</h3>
          <p className="m-0 text-sm leading-relaxed text-foreground/70">{card.description}</p>
        </div>
        {(card.sourceLabel || card.platform) && (
          <p className="m-0 text-xs text-foreground/50">
            {[card.sourceLabel, card.platform].filter(Boolean).join(" - ")}
          </p>
        )}
        {isError && (
          <p className="m-0 text-xs text-destructive">
            The linked stash is still available, but its visual data could not be loaded here.
          </p>
        )}
        {card.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {card.tags.map(tag => (
              <span
                key={tag}
                className="rounded-full border border-foreground/10 bg-background px-2 py-0.5 text-xs text-foreground/60"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        <Link
          to={STASH_DETAIL({ stashId: card.id })}
          className="mt-1 inline-flex w-fit items-center justify-center gap-2 rounded-md border border-foreground/10 px-3 py-2 text-sm font-medium text-foreground transition-colors hover:border-brand-500/40 hover:text-brand-500"
        >
          Open stash
          <ExternalLink className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  </aside>
);

// "restaurant" → "Restaurant"; short tags like "nyc"/"wfh" read as acronyms.
const titleCaseTag = (tag: string): string =>
  tag.length <= 3 ? tag.toUpperCase() : tag.replace(/\b\w/g, char => char.toUpperCase());

/**
 * Consolidated view for posts with several stash cards: the landing page's
 * "See what others are stashing" grid, scaled into the article column. Tag
 * pills filter the set (first tag = the card's theme); "All" is the default.
 */
const BlogStashCardGridBlock = ({
  cards,
  postsMap,
  isLoading,
  variant,
}: {
  cards: BlogStashCard[];
  postsMap?: Record<string, Post>;
  isLoading: boolean;
  variant?: "overview";
}) => {
  const [activeTheme, setActiveTheme] = useState<string | null>(null);
  const themes = [...new Set(cards.map(card => card.tags[0]).filter(Boolean))];
  const visibleCards = activeTheme ? cards.filter(card => card.tags[0] === activeTheme) : cards;

  const pillClass = (active: boolean) =>
    `shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
      active ? "bg-foreground text-background" : "bg-muted text-foreground hover:bg-muted/80"
    }`;

  return (
    <aside className="not-prose flex flex-col gap-4 rounded-lg border border-foreground/10 bg-card p-4 shadow-sm sm:p-5">
      <p className="m-0 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-brand-500">
        <Bookmark className="h-3.5 w-3.5" />
        Saved on Stasht
      </p>

      {themes.length >= 2 && (
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 sm:flex-wrap">
          <button type="button" onClick={() => setActiveTheme(null)} className={pillClass(activeTheme === null)}>
            All
          </button>
          {themes.map(theme => (
            <button
              key={theme}
              type="button"
              onClick={() => setActiveTheme(activeTheme === theme ? null : theme)}
              className={pillClass(activeTheme === theme)}
            >
              {titleCaseTag(theme)}
            </button>
          ))}
        </div>
      )}

      <div className={`grid grid-cols-2 gap-3 ${variant === "overview" ? "sm:grid-cols-4" : "sm:grid-cols-3"}`}>
        {visibleCards.map(card => {
          const post = postsMap?.[card.id];

          if (!post) {
            return (
              <div
                key={card.id}
                className="flex aspect-[4/5] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-foreground/15 bg-muted/30 p-3 text-center text-xs text-foreground/50"
              >
                {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImageOff className="h-5 w-5" />}
                <span className="line-clamp-3">{card.title}</span>
              </div>
            );
          }

          return (
            <StashThumbnail
              key={card.id}
              post={{ ...toThumbnailData(post), title: card.title, user_title: card.title }}
              href={STASH_DETAIL({ stashId: card.id })}
              width="full"
              cornerRadius="2xl"
              aspectRatio="aspect-[4/5]"
              showTitle
              hoverScale
              loading="lazy"
              platformIconPosition="top-right"
              overlay={
                card.tags[0] ? (
                  <span className="absolute left-2.5 top-2.5 inline-flex items-center rounded-full bg-black/50 px-2.5 py-1 text-xs font-medium text-white backdrop-blur-sm">
                    {titleCaseTag(card.tags[0])}
                  </span>
                ) : undefined
              }
            />
          );
        })}
      </div>
    </aside>
  );
};

/**
 * Resource-list sections ("Official links") as a compact bordered box instead
 * of a wall of bullet links: two columns on desktop, quiet link styling.
 */
const BlogLinksBoxBlock = ({ box }: { box: BlogLinksBox }) => (
  <aside className="not-prose rounded-lg border border-foreground/10 bg-foreground/[0.02] p-4 sm:p-5">
    {box.title && (
      <p className="m-0 mb-3 text-xs font-semibold uppercase tracking-wide text-foreground/55">{box.title}</p>
    )}
    <div
      className="[&_ul]:m-0 [&_ul]:grid [&_ul]:list-none [&_ul]:gap-x-8 [&_ul]:gap-y-2 [&_ul]:p-0 sm:[&_ul]:grid-cols-2 [&_li]:m-0 [&_li]:text-sm [&_li]:leading-relaxed [&_li]:text-foreground/75 [&_p]:m-0 [&_p]:text-sm [&_p]:leading-relaxed [&_a]:font-medium [&_a]:text-foreground [&_a]:underline [&_a]:decoration-brand-500/50 [&_a]:underline-offset-2 [&_a:hover]:text-brand-600"
      dangerouslySetInnerHTML={{ __html: box.html }}
    />
  </aside>
);

const isExampleTheme = (theme: string): theme is ExampleTheme =>
  THEME_LABELS.some(option => option.value === theme);

const BlogStashGridBlock = ({ grid }: { grid: BlogStashGrid }) => {
  const allowedThemes = grid.themes.filter(isExampleTheme);
  const themeOptions = allowedThemes.length > 0
    ? THEME_LABELS.filter(option => allowedThemes.includes(option.value))
    : THEME_LABELS;
  const [activeTheme, setActiveTheme] = useState<ExampleTheme | null>(null);

  const posts = useMemo(() => {
    const themeFiltered = allowedThemes.length > 0
      ? EXAMPLE_POSTS.filter(post => allowedThemes.includes(post.theme))
      : EXAMPLE_POSTS;
    const activeFiltered = activeTheme
      ? themeFiltered.filter(post => post.theme === activeTheme)
      : themeFiltered;

    return typeof grid.limit === "number" ? activeFiltered.slice(0, grid.limit) : activeFiltered;
  }, [activeTheme, allowedThemes, grid.limit]);

  return (
    <aside className="not-prose rounded-lg border border-foreground/10 bg-card p-4 sm:p-5 shadow-sm">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <h3 className="m-0 text-xl font-semibold leading-snug text-foreground">{grid.title}</h3>
          {grid.description && (
            <p className="m-0 text-sm leading-relaxed text-foreground/70">{grid.description}</p>
          )}
        </div>

        {grid.showFilters && themeOptions.length > 1 && (
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 sm:flex-wrap">
            {themeOptions.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => setActiveTheme(activeTheme === value ? null : value)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                  activeTheme === value
                    ? "bg-foreground text-background"
                    : "bg-muted text-foreground hover:bg-muted/80"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {posts.map(post => {
            const themeLabel = THEME_LABELS.find(theme => theme.value === post.theme)?.label;

            return (
              <Link
                key={post.id}
                to={STASH_DETAIL({ stashId: post.id })}
                className="group relative aspect-[4/5] overflow-hidden rounded-lg bg-muted"
              >
                <img
                  src={post.thumb}
                  alt={post.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/75" />
                {themeLabel && (
                  <span className="absolute left-2 top-2 inline-flex rounded-full bg-black/55 px-2 py-0.5 text-ui-badge font-semibold text-white backdrop-blur-sm">
                    {themeLabel}
                  </span>
                )}
                <div className="absolute right-2 top-2 z-10 flex items-center justify-center rounded-md border border-white/25 bg-black/55 p-1.5">
                  {getPlatformIcon(post.platform, 13, true)}
                </div>
                <div className="absolute inset-x-0 bottom-0 p-2.5">
                  <h4 className="m-0 line-clamp-2 text-sm font-semibold leading-tight text-white drop-shadow">
                    {post.title}
                  </h4>
                  {post.entities.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {post.entities.slice(0, 2).map(entity => (
                        <span
                          key={entity}
                          className="inline-flex rounded-full bg-white/20 px-1.5 py-0.5 text-ui-badge font-medium leading-none text-white/90 backdrop-blur-sm"
                        >
                          {entity}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </aside>
  );
};

/**
 * Store links inside post markdown are plain anchors injected via
 * dangerouslySetInnerHTML, so click tracking is delegated: one listener on
 * the article catches them all and fires the same GA events StoreBadges does.
 * Non-store links fall through untracked.
 */
const trackDelegatedStoreLinkClick = (event: MouseEvent<HTMLElement>) => {
  const anchor = event.target instanceof Element ? event.target.closest("a") : null;
  if (!anchor) return;
  if (anchor.dataset.marketingCtaTracked === "true") return;

  const destination = storeDestinationForUrl(anchor.href);
  if (!destination) return;

  trackMarketingCtaClick({
    destination,
    surface: "blog",
    placement: "body",
    label: anchor.textContent?.trim() || anchor.href,
    href: anchor.href,
  });
};

export const BlogContentRenderer = ({ blocks }: { blocks: BlogContentBlock[] }) => {
  const stashCardIds = useMemo(() => {
    const ids = new Set<string>();
    blocks.forEach(block => {
      if (block.type === "stash-card") ids.add(block.card.id);
      if (block.type === "stash-card-grid") block.cards.forEach(card => ids.add(card.id));
      if (block.type === "collection-card") block.card.previewStashIds.forEach(id => ids.add(id));
    });
    return [...ids];
  }, [blocks]);

  const { data: postsMap, isLoading, isError } = useQuery({
    queryKey: ["blog-stash-cards", stashCardIds],
    queryFn: async () => {
      if (stashCardIds.length === 0) return {};
      const response = await postsService.batchGetPosts(stashCardIds, true);
      return response.posts ?? {};
    },
    enabled: stashCardIds.length > 0,
    staleTime: 5 * 60_000,
  });

  return (
    <article className="flex flex-col gap-7" onClick={trackDelegatedStoreLinkClick}>
      {blocks.map((block, index) => {
        if (block.type === "inline-cta") {
          return <BlogInlineCta key={`inline-cta-${index}`} />;
        }

        if (block.type === "stash-card") {
          return (
            <BlogStashCardBlock
              key={`${block.card.id}-${index}`}
              card={block.card}
              post={postsMap?.[block.card.id]}
              isLoading={isLoading}
              isError={isError}
            />
          );
        }

        if (block.type === "stash-card-grid") {
          return (
            <BlogStashCardGridBlock
              key={`stash-card-grid-${index}`}
              cards={block.cards}
              postsMap={postsMap}
              isLoading={isLoading}
              variant={block.variant}
            />
          );
        }

        if (block.type === "collection-card") {
          return (
            <BlogCollectionCardBlock
              key={`${block.card.id}-${index}`}
              card={block.card}
              postsMap={postsMap}
              arePreviewPostsLoading={isLoading}
              arePreviewPostsUnavailable={isError}
            />
          );
        }

        if (block.type === "stash-grid") {
          return <BlogStashGridBlock key={`stash-grid-${index}`} grid={block.grid} />;
        }

        if (block.type === "links-box") {
          return <BlogLinksBoxBlock key={`links-box-${index}`} box={block.box} />;
        }

        return (
          <div
            key={`markdown-${index}`}
            className="prose prose-lg prose-legal prose-blog max-w-[70ch] text-pretty prose-headings:font-semibold prose-headings:text-balance"
            dangerouslySetInnerHTML={{ __html: block.html }}
          />
        );
      })}
    </article>
  );
};
